/**
 * Cloud capability gates — Phase 1 Mirror + Phase 2 Hybrid.
 * Local Dexie stays SoT; Supabase sync only when env + business cloud_plan allow it.
 */

import { kvGet } from '../infrastructure/storage/kvAccess';
import { readOfflineBusinessesMergedSync } from '../utils/authCredentialRead';
import { normalizeBusinessStorageId } from '../utils/sharedCore';

export const CLOUD_PLAN = Object.freeze({
  NONE: 'none',
  MIRROR: 'mirror',
  HYBRID: 'hybrid'
});

/** Plans that may push Mirror entities (sales / purchases / reports / orders). */
const MIRROR_PUSH_PLANS = new Set([CLOUD_PLAN.MIRROR, CLOUD_PLAN.HYBRID]);

export function normalizeCloudPlan(value) {
  const v = String(value || CLOUD_PLAN.NONE)
    .trim()
    .toLowerCase();
  if (v === CLOUD_PLAN.MIRROR || v === CLOUD_PLAN.HYBRID) return v;
  return CLOUD_PLAN.NONE;
}

export function isCloudMirrorPushEnabled(plan) {
  return MIRROR_PUSH_PLANS.has(normalizeCloudPlan(plan));
}

/** Multi-device catalog (products / customers / suppliers / lots). */
export function isCloudHybridSyncEnabled(plan) {
  return normalizeCloudPlan(plan) === CLOUD_PLAN.HYBRID;
}

/**
 * Global entity sync gate (Mirror/Hybrid push/pull).
 * Explicit false|0 opts out. Explicit true|1 opts in.
 * When unset, enable automatically if Supabase URL + anon key are configured
 * (avoids “logged in but never syncs” from a forgotten build flag).
 */
export function entitySyncEnvEnabled() {
  const v = process.env.REACT_APP_ENABLE_SUPABASE_ENTITY_SYNC;
  if (v === 'false' || v === '0') return false;
  if (v === 'true' || v === '1') return true;
  return Boolean(
    process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY
  );
}

export function isValidCloudBusinessId(businessId) {
  if (!businessId || typeof businessId !== 'string') return false;
  const id = businessId.trim();
  if (!id) return false;
  if (id === 'admin_user' || id.startsWith('admin-')) return false;
  return true;
}

/**
 * Resolve cloud_plan from session / offline catalog for a local business id.
 * Missing plan → none (local-only, unchanged behaviour for non-add-on customers).
 */
export function readLocalCloudPlan(businessId) {
  const localId = normalizeBusinessStorageId(businessId);
  try {
    const currentUser = kvGet('currentUser');
    const sessionBiz = currentUser?.business;
    if (sessionBiz) {
      const sessionLocalId = normalizeBusinessStorageId(sessionBiz.id);
      if (
        !localId ||
        sessionLocalId === localId ||
        String(sessionBiz.cloudId || '') === localId ||
        normalizeBusinessStorageId(sessionBiz.cloudId) === localId
      ) {
        if (sessionBiz.cloud_plan != null) {
          return normalizeCloudPlan(sessionBiz.cloud_plan);
        }
      }
    }

    const currentBusiness = kvGet('currentBusiness');
    if (currentBusiness) {
      const cbId = normalizeBusinessStorageId(currentBusiness.id || currentBusiness.businessId);
      if (
        !localId ||
        cbId === localId ||
        String(currentBusiness.cloudId || '') === localId ||
        normalizeBusinessStorageId(currentBusiness.cloudId) === localId
      ) {
        if (currentBusiness.cloud_plan != null) {
          return normalizeCloudPlan(currentBusiness.cloud_plan);
        }
      }
    }

    if (localId) {
      // ADR-0002: authCredentialRead merges KV ∪ native LS — never call localStorage here
      const businesses = readOfflineBusinessesMergedSync();
      if (Array.isArray(businesses)) {
        const found = businesses.find(
          (b) =>
            normalizeBusinessStorageId(b?.id) === localId ||
            normalizeBusinessStorageId(b?.cloudId) === localId ||
            String(b?.cloudId || '') === localId ||
            String(b?.id || '') === localId ||
            String(b?.id || '') === `business_${localId}`
        );
        if (found?.cloud_plan != null) {
          return normalizeCloudPlan(found.cloud_plan);
        }
      }
    }
  } catch {
    /* optional */
  }
  return CLOUD_PLAN.NONE;
}

/**
 * @returns {{ allowed: boolean, reason?: string, plan: string }}
 */
export function assertMirrorSyncAllowed(businessId) {
  if (!entitySyncEnvEnabled()) {
    return { allowed: false, reason: 'env_off', plan: CLOUD_PLAN.NONE };
  }
  const plan = readLocalCloudPlan(businessId);
  if (!isCloudMirrorPushEnabled(plan)) {
    return { allowed: false, reason: 'plan_none', plan };
  }
  return { allowed: true, plan };
}

/**
 * Phase 2 — Hybrid catalog sync (includes Mirror push + catalog pull/push).
 * @returns {{ allowed: boolean, reason?: string, plan: string }}
 */
export function assertHybridSyncAllowed(businessId) {
  if (!entitySyncEnvEnabled()) {
    return { allowed: false, reason: 'env_off', plan: CLOUD_PLAN.NONE };
  }
  const plan = readLocalCloudPlan(businessId);
  if (!isCloudHybridSyncEnabled(plan)) {
    return { allowed: false, reason: 'plan_not_hybrid', plan };
  }
  return { allowed: true, plan };
}
